import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language } from '@i18n';
import { SUPPORTED_LANGUAGES } from '@i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, defaultValue?: string) => string;
}
const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { i18n, t: i18nT } = useTranslation();

  // Initialize language from localStorage or i18next current language
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('language');
    if (stored && SUPPORTED_LANGUAGES.includes(stored as Language)) {
      return stored as Language;
    }
    return i18n.language as Language;
  });

  // Sync with i18next when language changes
  useEffect(() => {
    if(document.documentElement.lang != language) {
      document.documentElement.lang = language;
    }

    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Save to localStorage and update document lang attribute
  const setLanguage = (lang: Language) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`Language "${lang}" is not supported. Falling back to Italian.`);
      lang = 'it';
    }

    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
  };

  // Wrapper for i18next translation function with fallback
  const t = (key: string, defaultValue: string = key): string => {
    try {
      const translated = i18nT(key);
      // If translation not found, i18next returns the key itself
      return translated === key && defaultValue !== key ? defaultValue : translated;
    } catch (error) {
      console.warn(`Translation error for key: ${key}`, error);
      return defaultValue;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
